import { call, put, takeLatest } from "redux-saga/effects";
import { startLoading, stopLoading } from "@shared/store/actions";
import {
  ActionWithPayload,
  PaginatedResponse,
  PropertiesFilter,
  ResponseError,
  WithCallback,
} from "@shared/interfaces";
import { showHttpErrorNotification } from "@shared/utils";
import { Property } from "@shared/interfaces/Property";
import { download } from "@shared/utils/request";
import { actions } from "@containers/PayPerLead/store";

import {
  assignedToLead,
  checkIfPropertyCouldBeAssignedToLead,
  getProperties,
  getProperty,
  getPropertiesCsv,
} from "./actions";
import api from "../api";

function* getPropertiesSaga({ payload }: ActionWithPayload<PropertiesFilter>) {
  try {
    yield put(startLoading());
    const response: PaginatedResponse<Property> = yield call(api.getProperties, payload);
    yield put(getProperties.success(response));
  } catch (error) {
    yield put(getProperties.failure(error as Error));
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* getPropertiesCsvSaga({ payload }: ActionWithPayload<PropertiesFilter>) {
  try {
    yield put(startLoading());

    const response: Blob = yield call(api.getPropertiesCsv, payload);
    const urlObj = window.URL.createObjectURL(response);

    yield call(download, urlObj, "properties.csv");
  } catch (error) {
    yield put(getProperties.failure(error as Error));
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* getPropertySaga({ payload }: ActionWithPayload<number>) {
  try {
    yield put(startLoading());
    const response: Property = yield call(api.getProperty, payload);
    yield put(getProperty.success(response));
  } catch (error) {
    yield put(getProperty.failure(error as Error));
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* checkIfPropertyCouldBeAssignedToLeadSaga({ payload }: ActionWithPayload<number>) {
  try {
    yield put(startLoading());
    const response: Property = yield call(api.checkIfPropertyCouldBeAssignedToLead, payload);
    yield put(checkIfPropertyCouldBeAssignedToLead.success(response));
  } catch (error) {
    yield put(checkIfPropertyCouldBeAssignedToLead.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

function* assignedToLeadSage({ payload }: ActionWithPayload<WithCallback<{ propertyId: number; leadId: number }>>) {
  try {
    const { callback, ...body } = payload;

    yield put(startLoading());

    yield call(api.assignedToLead, body);

    yield put(actions.getLead.request(payload.leadId.toString()));

    callback?.();
  } catch (error) {
    yield put(checkIfPropertyCouldBeAssignedToLead.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

function* propertiesSagas() {
  yield takeLatest(getProperties.request, getPropertiesSaga);
  yield takeLatest(getPropertiesCsv.request, getPropertiesCsvSaga);
  yield takeLatest(getProperty.request, getPropertySaga);
  yield takeLatest(checkIfPropertyCouldBeAssignedToLead.request, checkIfPropertyCouldBeAssignedToLeadSaga);
  yield takeLatest(assignedToLead.request, assignedToLeadSage);
}

export default propertiesSagas;
